import { useState } from "react";
import { Minus, Plus, Trash2, ShoppingBag, Mail } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { useCart } from "./cart";
import { EMAIL, eur, whatsapp } from "./data";

const PAYMENTS = ["Ricarica Postepay / carta prepagata", "Da concordare"];
const FIELDS = [
  { k: "nome", label: "Nome e cognome", type: "text" },
  { k: "telefono", label: "Telefono", type: "tel" },
  { k: "indirizzo", label: "Indirizzo di consegna", type: "text" },
];

const Line = ({ it, set }) => (
  <li data-testid={`cart-item-${it.id}`} className="flex items-center gap-3 py-4 border-b border-[#2A1C15]/10">
    <div className="flex-1 min-w-0">
      <p className="font-serif text-lg leading-tight">{it.name}</p>
      <p className="text-xs text-[#2A1C15]/70">{it.spec}</p>
    </div>
    <div className="flex items-center rounded-full border border-[#2A1C15]/20">
      <button data-testid={`cart-dec-${it.id}`} onClick={() => set(it.id, it.qty - 1)} className="p-2 hover:text-[#C9692C]" aria-label="Diminuisci"><Minus className="w-3.5 h-3.5" /></button>
      <span data-testid={`cart-qty-${it.id}`} className="w-6 text-center text-sm font-semibold">{it.qty}</span>
      <button data-testid={`cart-inc-${it.id}`} onClick={() => set(it.id, it.qty + 1)} className="p-2 hover:text-[#C9692C]" aria-label="Aumenta"><Plus className="w-3.5 h-3.5" /></button>
    </div>
    <p className="w-20 text-right font-semibold text-sm">{eur(it.qty * it.price)}</p>
    <button data-testid={`cart-remove-${it.id}`} onClick={() => set(it.id, 0)} className="p-1 text-[#2A1C15]/60 hover:text-[#C9692C]" aria-label="Rimuovi"><Trash2 className="w-4 h-4" /></button>
  </li>
);

const buildMessage = (items, total, form) =>
  [
    "Ciao Pellet Italiana, vorrei ordinare:",
    ...items.map((i) => `- ${i.qty} × ${i.name} (${i.spec}) = ${eur(i.qty * i.price)}`),
    `Totale: ${eur(total)} (IVA e trasporto esclusi)`,
    `Pagamento: ${form.pagamento}`,
    `Nome: ${form.nome}`,
    `Telefono: ${form.telefono}`,
    `Indirizzo: ${form.indirizzo}`,
    form.note ? `Note: ${form.note}` : "",
  ].filter(Boolean).join("\n");

export const CartDrawer = () => {
  const { items, total, open, setOpen, set } = useCart();
  const [form, setForm] = useState({ nome: "", telefono: "", indirizzo: "", note: "", pagamento: PAYMENTS[0] });
  const ready = items.length > 0 && form.nome.trim() && form.telefono.trim() && form.indirizzo.trim();
  const msg = buildMessage(items, total, form);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#FDFBF7] text-[#2A1C15] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#2A1C15]/10 text-left">
          <SheetTitle className="font-serif text-3xl font-normal text-[#2A1C15]">Il tuo carrello</SheetTitle>
          <SheetDescription className="text-[#2A1C15]/70">Pellet in vero legno di abete · consegna in 24/48h</SheetDescription>
        </SheetHeader>

        <div data-lenis-prevent className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="py-16 text-center text-[#2A1C15]/70">
              <ShoppingBag className="w-10 h-10 mx-auto mb-4 text-[#C9692C]" />
              Il carrello è vuoto.
            </div>
          ) : (
            <>
              <ul>{items.map((it) => <Line key={it.id} it={it} set={set} />)}</ul>
              <div className="py-5 flex items-end justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-[#2A1C15]/70">Totale<br />IVA e trasporto esclusi</span>
                <span data-testid="cart-total" className="font-serif text-4xl">{eur(total)}</span>
              </div>
              <div className="space-y-3 pb-6">
                {FIELDS.map((f) => (
                  <input key={f.k} type={f.type} placeholder={`${f.label} *`} value={form[f.k]} onChange={upd(f.k)} data-testid={`cart-input-${f.k}`}
                    className="w-full rounded-xl border border-[#2A1C15]/20 bg-white px-4 py-3 text-sm outline-none focus:border-[#C9692C]" />
                ))}
                <textarea placeholder="Note (orari, piano, accesso camion...)" value={form.note} onChange={upd("note")} rows={2} data-testid="cart-input-note"
                  className="w-full rounded-xl border border-[#2A1C15]/20 bg-white px-4 py-3 text-sm outline-none focus:border-[#C9692C]" />
                <p className="pt-2 text-xs uppercase tracking-[0.2em] text-[#2A1C15]/70">Pagamento</p>
                {PAYMENTS.map((p) => (
                  <label key={p} data-testid={`cart-payment-${p.startsWith("Ricarica") ? "postepay" : "concordare"}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors ${form.pagamento === p ? "border-[#C9692C] bg-[#E8893F]/10" : "border-[#2A1C15]/20"}`}>
                    <input type="radio" name="pagamento" checked={form.pagamento === p} onChange={() => setForm((f) => ({ ...f, pagamento: p }))} className="accent-[#C9692C]" />
                    {p}
                  </label>
                ))}
                <p className="text-xs text-[#2A1C15]/70 leading-relaxed">Dopo l'invio ti ricontattiamo per confermare disponibilità, costo del trasporto e dati per il pagamento.</p>
              </div>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-[#2A1C15]/10 space-y-3">
            <a href={ready ? whatsapp(msg) : undefined} target="_blank" rel="noopener noreferrer" data-testid="cart-send-whatsapp"
              aria-disabled={!ready}
              className={`flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-colors ${ready ? "bg-[#25D366] text-[#0B2A17] hover:bg-[#1fbf5a]" : "bg-[#2A1C15]/10 text-[#2A1C15]/50 pointer-events-none"}`}>
              Invia ordine su WhatsApp
            </a>
            <a href={ready ? `mailto:${EMAIL}?subject=${encodeURIComponent("Ordine pellet")}&body=${encodeURIComponent(msg)}` : undefined} data-testid="cart-send-email"
              aria-disabled={!ready}
              className={`flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold border transition-colors ${ready ? "border-[#2A1C15] hover:bg-[#2A1C15] hover:text-[#FDFBF7]" : "border-[#2A1C15]/20 text-[#2A1C15]/50 pointer-events-none"}`}>
              <Mail className="w-4 h-4" /> Invia ordine via email
            </a>
            {!ready && <p data-testid="cart-required-hint" className="text-center text-xs text-[#2A1C15]/70">Compila nome, telefono e indirizzo per inviare l'ordine.</p>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
